import React, { useRef, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError, {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'
import { selectors as siteSelectors } from 'store/siteManager'
import {
  operations as ssapiOperations,
  selectors as ssapiSelectors,
} from 'store/ssapiManager'
import { PLAYBOOK_STEP_MAP } from 'utils/adminExperienceHelpers'
import ImplementationApproachModal from './component'
import { ENTRY_ID, fieldNames, validationSchema } from './data'

export const ImplementationApproachModalContainer = ({
  clearErrors,
  fetchPublishFlows,
  flowLabel,
  handleCancel,
  handleClose,
  hasApiError,
  modals,
  publishFlow,
  postPublishFlows,
  site,
  structureVersion,
  updatePublishFlows,
}) => {
  const isMountedRef = useRef(true)

  useEffect(() => {
    if (site?.id) {
      fetchPublishFlows(site.id)
    }
  }, [site?.id])

  useEffect(() => {
    // Track mount status
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [publishFlow])

  if (!modals) return null

  const closeModal = async () => {
    clearErrors()

    // Close modal - let Redux state updates handle data refresh
    // Removing fetchUserSites/fetchUserContext calls to avoid race conditions
    // where state reloads before saved preferences are reflected
    if (isMountedRef.current) {
      handleClose()
    }
  }

  const getInitialValues = publishFlowItems => {
    const initialValues = {}

    fieldNames.forEach(field => {
      initialValues[field] =
        publishFlowItems?.find(item => item.name === field)?.completeValue ||
        null
    })

    return initialValues
  }

  const handleSave = async (values, isComplete) => {
    const postBody = {
      flowName: PLAYBOOK_STEP_MAP.flowName,
      name: PLAYBOOK_STEP_MAP.name,
      publishDate: PLAYBOOK_STEP_MAP.publishDate,
      siteId: site.id,
      structureVersion,
    }
    const updateBody = {
      id: publishFlow?.id,
      publishDate: PLAYBOOK_STEP_MAP.publishDate,
      items: [],
      siteId: site.id,
    }

    updateBody.items = fieldNames.map(field => ({
      name: field,
      completeValue: values[field],
      draftValue: values[field],
    }))

    if (Object.keys(publishFlow).length < 1) {
      const { id } = await postPublishFlows(postBody)
      updateBody.id = id
      await updatePublishFlows(id, updateBody)
      await fetchPublishFlows(site.id)
    } else {
      await updatePublishFlows(publishFlow?.id, updateBody)
      await fetchPublishFlows(site.id)
    }
  }

  const modalContent = modals[0]

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(publishFlow?.publishFlowItems)}
      validationSchema={validationSchema}
    >
      {({
        errors,
        isSubmitting,
        setFieldTouched,
        setFieldValue,
        setSubmitting,
        setTouched,
        touched,
        values,
      }) => (
        <ImplementationApproachModal
          choiceContent={modalContent.choiceContent}
          errors={errors}
          flowLabel={flowLabel}
          handleCancel={handleCancel}
          handleChange={(name, value) => {
            setFieldTouched(name)
            setFieldValue(name, value)
          }}
          handleClose={handleClose}
          handleNextButtonClick={async () => {
            const isValidSync = validationSchema.isValidSync(values)
            if (!isValidSync) {
              const touchedFields = fieldNames.reduce((acc, name) => {
                acc[name] = true
                return acc
              }, {})
              await setTouched(touchedFields)
              return
            }
            setSubmitting(true)
            // ImplementationApproachModal is single-step, so always save as finalized
            await handleSave(values, true)
            setSubmitting(false)
            await closeModal()
          }}
          hasApiError={hasApiError}
          header={modalContent.header}
          helpCopy={modalContent.helpCopy}
          isSubmitting={isSubmitting}
          modals={modals}
          nextLabel={modalContent.nextLabel}
          setSubmitting={setSubmitting}
          touched={touched}
          values={values}
        />
      )}
    </Formik>
  )
}

ImplementationApproachModalContainer.propTypes = {
  clearErrors: PropTypes.func.isRequired,
  fetchPublishFlows: PropTypes.func.isRequired,
  flowLabel: PropTypes.string.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  hasApiError: PropTypes.bool.isRequired,
  modals: PropTypes.array.isRequired,
  postPublishFlows: PropTypes.func.isRequired,
  publishFlow: PropTypes.object,
  site: PropTypes.object.isRequired,
  structureVersion: PropTypes.number,
  updatePublishFlows: PropTypes.func.isRequired,
}

const mapStateToProps = (state, ownProps) => {
  const { error, structureVersion } = state.ssapiManager
  const site = siteSelectors.selectSiteById(state, ownProps.siteId)
  const publishFlows = ssapiSelectors.selectPublishFlowsBySiteId(
    state,
    ownProps.siteId,
  )
  const publishFlow =
    publishFlows?.find(
      flow => flow.publishFlowStructureId === structureVersion,
    ) || {}

  return {
    hasApiError: !!error,
    publishFlow,
    site,
  }
}

const mapDispatchToProps = {
  clearErrors: ssapiOperations.clearErrors,
  fetchPublishFlows: ssapiOperations.fetchPublishFlows,
  postPublishFlows: ssapiOperations.postPublishFlows,
  updatePublishFlows: ssapiOperations.updatePublishFlows,
}

const mapper = entry => {
  const { flowLabel, modals } = entry || {}
  return {
    flowLabel,
    modals,
  }
}

const options = {
  entryId: ENTRY_ID,
  include: 3,
  mapper,
  spread: true,
}

export default ContentEntryDataGetter(
  withApiError(
    ImplementationApproachModalContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
